import {
  DEFAULT_GRID_COLUMNS,
  DEFAULT_GRID_ROWS,
} from '../components/PageConfigProvider/constants';
import type {
  PrimaryFilterConfig,
  TabContent,
} from '../components/PageConfigProvider/page-reducer';
import {
  type Page,
  PageType,
  RevelioPageId,
} from '../components/PageConfigProvider/types';
import { FilterId } from '../filters/types/filters';

const geographyCompositionV2Snapshot: TabContent['items'] = [
  {
    position: { x: 0, width: 12, y: 0, height: 8 },
    id: 'geography-composition-v2-snapshot-headcount',
    title: 'Headcount',
    info: {
      text: 'Our estimate of the total workforce. These counts include subsidiaries and may include both formal employees and contingent workers, depending on your preference settings. More information on the methodologies used for this metric can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#sampling-weights',
        },
      ],
    },
    plot: {
      defaultMetricOptions: ['headcount'],
      chartType: 'bar',
      adaptive: true,
    },
  },
  {
    position: { x: 12, width: 12, y: 0, height: 8 },
    id: 'geography-composition-v2-snapshot-growth-rate',
    title: 'Growth Rate',
    info: {
      text: 'The growth rate is the percent change in the total workforce. It is the difference between the hiring rate and attrition rate.',
    },
    plot: {
      defaultMetricOptions: ['growth_rate'],
      chartType: 'bar',
      adaptive: true,
    },
  },
  {
    position: { x: 24, width: 12, y: 0, height: 8 },
    id: 'geography-composition-v2-snapshot-hiring-rate',
    title: 'Hiring Rate',
    info: {
      text: 'The annual rate at which workers join, relative to the total headcount. It is the 12-month moving sum of inflows divided by the 12-month moving average of headcount.',
    },
    plot: {
      defaultMetricOptions: ['hiring_rate'],
      chartType: 'bar',
      adaptive: true,
    },
  },
  {
    position: { x: 36, width: 12, y: 0, height: 8 },
    id: 'geography-composition-v2-snapshot-attrition-rate',
    title: 'Attrition Rate',
    info: {
      text: 'The annual rate at which workers leave, relative to the total headcount. It is the 12-month moving sum of outflows divided by the 12-month moving average of headcount.',
    },
    plot: {
      defaultMetricOptions: ['attrition_rate'],
      chartType: 'bar',
      adaptive: true,
    },
  },
  {
    position: { x: 0, width: 24, y: 8, height: 14 },
    id: 'geography-composition-v2-snapshot-role',
    title: 'Role',
    info: {
      text: 'The composition of workers by occupation. More information on the job representation and taxonomy can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#jobs-taxonomy',
        },
      ],
    },
    plot: {
      secondaryDimension: 'role',
      defaultMetricOptions: ['headcount'],
      chartType: 'stacked-bar',
      adaptive: true,
      treemap: { sizeTransform: 'sqrt' },
    },
  },
  {
    position: { x: 24, width: 24, y: 8, height: 14 },
    id: 'geography-composition-v2-snapshot-industry',
    title: 'Industry',
    info: {
      text: 'The composition of workers by industry.',
    },
    plot: {
      secondaryDimension: 'rics',
      defaultMetricOptions: ['headcount'],
      chartType: 'stacked-bar',
      adaptive: true,
      treemap: { sizeTransform: 'sqrt' },
    },
  },
  {
    position: { x: 0, width: 24, y: 22, height: 14 },
    id: 'geography-composition-v2-snapshot-skill',
    title: 'Skill',
    info: {
      text: 'The composition of workers by skill. More information on the skill model can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#combining-skill-clusters',
        },
      ],
    },
    plot: {
      secondaryDimension: 'skills',
      defaultMetricOptions: ['headcount'],
      chartType: 'stacked-bar',
      treemap: { sizeTransform: 'sqrt' },
      adaptive: true,
      hideOther: true,
    },
  },
  {
    position: { x: 24, width: 24, y: 22, height: 14 },
    id: 'geography-composition-v2-snapshot-seniority',
    title: 'Seniority',
    info: {
      text: 'The composition of workers by seniority. More information on the seniority model can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#seniority',
        },
      ],
    },
    plot: {
      secondaryDimension: 'seniority',
      defaultMetricOptions: ['headcount'],
      chartType: 'stacked-bar',
      adaptive: true,
      treemap: { sizeTransform: 'sqrt' },
    },
  },
];

const geographyCompositionV2Overtime: TabContent['items'] = [
  {
    position: { x: 0, width: 12, y: 0, height: 8 },
    id: 'geography-composition-v2-overtime-headcount',
    title: 'Headcount',
    info: {
      text: 'Our estimate of the total workforce. These counts include subsidiaries and may include both formal employees and contingent workers, depending on your preference settings. More information on the methodologies used for this metric can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#sampling-weights',
        },
      ],
    },
    plot: {
      defaultMetricOptions: ['headcount'],
      chartType: 'line',
    },
  },
  {
    position: { x: 12, width: 12, y: 0, height: 8 },
    id: 'geography-composition-v2-overtime-growth-rate',
    title: 'Growth Rate',
    info: {
      text: 'The growth rate is the percent change in the total workforce. It is the difference between the hiring rate and attrition rate.',
    },
    plot: {
      defaultMetricOptions: ['growth_rate'],
      chartType: 'line',
    },
  },
  {
    position: { x: 24, width: 12, y: 0, height: 8 },
    id: 'geography-composition-v2-overtime-hiring-rate',
    title: 'Hiring Rate',
    info: {
      text: 'The annual rate at which workers join, relative to the total headcount. It is the 12-month moving sum of inflows divided by the 12-month moving average of headcount.',
    },
    plot: {
      defaultMetricOptions: ['hiring_rate'],
      chartType: 'line',
    },
  },
  {
    position: { x: 36, width: 12, y: 0, height: 8 },
    id: 'geography-composition-v2-overtime-attrition-rate',
    title: 'Attrition Rate',
    info: {
      text: 'The annual rate at which workers leave, relative to the total headcount. It is the 12-month moving sum of outflows divided by the 12-month moving average of headcount.',
    },
    plot: {
      defaultMetricOptions: ['attrition_rate'],
      chartType: 'line',
    },
  },
  {
    position: { x: 0, width: 24, y: 8, height: 14 },
    id: 'geography-composition-v2-overtime-role',
    title: 'Role',
    info: {
      text: 'The composition of workers by occupation. More information on the job representation and taxonomy can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#jobs-taxonomy',
        },
      ],
    },
    plot: {
      secondaryDimension: 'role',
      defaultMetricOptions: ['share_of_headcount'],
      chartType: 'share-of-segment',
    },
  },
  {
    position: { x: 24, width: 24, y: 8, height: 14 },
    id: 'geography-composition-v2-overtime-industry',
    title: 'Industry',
    info: {
      text: 'The composition of workers by industry.',
    },
    plot: {
      secondaryDimension: 'rics',
      defaultMetricOptions: ['share_of_headcount'],
      chartType: 'share-of-segment',
    },
  },
  {
    position: { x: 0, width: 24, y: 22, height: 14 },
    id: 'geography-composition-v2-overtime-skill',
    title: 'Skill',
    info: {
      text: 'The composition of workers by skill. More information on the skill model can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#combining-skill-clusters',
        },
      ],
    },
    plot: {
      secondaryDimension: 'skills',
      defaultMetricOptions: ['share_of_headcount'],
      chartType: 'share-of-segment',
    },
  },
  {
    position: { x: 24, width: 24, y: 22, height: 14 },
    id: 'geography-composition-v2-overtime-seniority',
    title: 'Seniority',
    info: {
      text: 'The composition of workers by seniority. More information on the seniority model can be found <a>.',
      hyperlinks: [
        {
          text: 'here',
          href: 'https://www.data-dictionary.reveliolabs.com/methodology.html#seniority',
        },
      ],
    },
    plot: {
      secondaryDimension: 'seniority',
      defaultMetricOptions: ['share_of_headcount'],
      chartType: 'share-of-segment',
    },
  },
];

const sharedConfig: Pick<TabContent, 'grid'> & {
  primaryFilter: PrimaryFilterConfig;
} = {
  primaryFilter: {
    id: FilterId.GEOGRAPHY,
    selectionLimit: 6,
    selectionDefault: 5,
  },
  grid: { rows: DEFAULT_GRID_ROWS, columns: DEFAULT_GRID_COLUMNS },
};

const geographyCompositionV2SnapshotTab: TabContent = {
  id: 'geography-composition-v2-snapshot',
  items: geographyCompositionV2Snapshot,
  ...sharedConfig,
  secondaryFilters: [
    FilterId.SNAPSHOT_DATE,
    FilterId.EDUCATION,
    FilterId.ETHNICITY,
    FilterId.GENDER,
    FilterId.COMPANY,
    FilterId.ROLE,
    FilterId.SKILL,
    FilterId.CUSTOM_SKILL,
    FilterId.SENIORITY,
    FilterId.KEYWORDS,
    FilterId.TITLE,
    FilterId.DATA_ADJUSTMENTS,
    FilterId.CONTINGENT_WORKERS,
  ].map((id) => ({ id })),
};

const geographyCompositionV2OvertimeTab: TabContent = {
  id: 'geography-composition-v2-overtime',
  items: geographyCompositionV2Overtime,
  ...sharedConfig,
  secondaryFilters: [
    FilterId.DATE_RANGE,
    FilterId.EDUCATION,
    FilterId.ETHNICITY,
    FilterId.GENDER,
    FilterId.COMPANY,
    FilterId.ROLE,
    FilterId.SKILL,
    FilterId.CUSTOM_SKILL,
    FilterId.SENIORITY,
    FilterId.KEYWORDS,
    FilterId.TITLE,
    FilterId.DATA_ADJUSTMENTS,
    FilterId.CONTINGENT_WORKERS,
  ].map((id) => ({ id })),
  primaryFilter: {
    ...sharedConfig.primaryFilter,
    tagColors: true,
  },
};

export const geographyCompositionV2: Page = {
  type: PageType.REVELIO_PUBLIC,
  id: RevelioPageId.GEOGRAPHY_COMPOSITION_V2,
  revelioPageId: RevelioPageId.GEOGRAPHY_COMPOSITION_V2,
  name: 'Compositions',
  owner: { id: 'revelio-labs', username: 'revelio-labs' },
  tabs: [
    {
      id: 'geography-composition-v2-snapshot',
      label: 'Snapshot',
      tabContent: geographyCompositionV2SnapshotTab,
      isActive: true,
    },
    {
      id: 'geography-composition-v2-overtime',
      label: 'Over Time',
      tabContent: geographyCompositionV2OvertimeTab,
      isActive: false,
    },
  ],
};
